import classNames from 'classnames';
import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';
import { ErrorLabel, SuccessLabel } from '@eika/label';

import { Format } from '@/domain/app';
import { PendingCardTokenActionResult, PendingCardTokenForUI } from '@/domain/digital-wallet';

import useText from '@hooks/use-text';

type Props = {
    status: PendingCardTokenActionResult | undefined;
    pendingCardToken: PendingCardTokenForUI;
    activateCardToken: (cardId: string) => void;
    cardInfoLoading: boolean;
};

const PendingCardTokenItem: React.FC<Props> = ({ status, pendingCardToken, activateCardToken, cardInfoLoading }) => {
    const text = useText();

    return (
        <li className="pending-card-list__item" data-testid="pending-cards-list-item">
            {cardInfoLoading ? (
                <Skeleton className="pending-card-list__item-image" />
            ) : (
                <img
                    className="pending-card-list__item-image"
                    alt="illustrert bilde av kortet"
                    src={pendingCardToken.imageUrl}
                />
            )}

            {cardInfoLoading ? (
                <div className="pending-card-list__item-content">
                    <Skeleton height={42} />
                    <Skeleton height={21} />
                </div>
            ) : (
                <div className="pending-card-list__item-content">
                    <span
                        className="pending-card-list__item-collapsable-text"
                        data-testid="pending-card-list-item-card-name"
                    >
                        {pendingCardToken.cardName ?? 'Kortnavn'}
                    </span>
                    {pendingCardToken.cardNumber && (
                        <span>{Format.cardNumber(pendingCardToken.cardNumber, { maskedNumberReplacement: '•' })}</span>
                    )}
                </div>
            )}

            <div
                className={classNames('pending-card-list__item-button-wrapper', {
                    'pending-card-list__item-button-wrapper--loading': status === 'loading',
                })}
            >
                {status === 'success' ? (
                    <SuccessLabel
                        text={text.activated}
                        icon={IconTypes.USP_CHECKMARK_24}
                        data-testid="pending-cards-list-success-label"
                    />
                ) : (
                    <ButtonWithSpinner
                        size="small"
                        buttonType="negative"
                        disabled={cardInfoLoading}
                        showSpinner={status === 'loading'}
                        onClick={() => activateCardToken(pendingCardToken.tokenId)}
                        data-testid={
                            status === 'loading'
                                ? 'pending-cards-list-activate-button-loading'
                                : 'pending-cards-list-activate-button'
                        }
                    >
                        {text.activate}
                    </ButtonWithSpinner>
                )}
            </div>
            {status === 'error' && (
                <div className="pending-card-list__item--error">
                    <ErrorLabel
                        data-testid="pending-cards-list-error-label"
                        icon={IconTypes.UTROPSTEGN_24}
                        text={text.couldNotActivateCard}
                    />
                </div>
            )}
        </li>
    );
};

export default PendingCardTokenItem;
